import React, { useState, useContext, useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import AuthModal from '../auth/AuthModal';
import { ThemeContext } from '../../contexts/ThemeContext';
import { FaSun, FaMoon, FaUniversity } from 'react-icons/fa';
import { useUniversity } from '../../contexts/UniversityContext';
import UniversitySelectModal from '../university/UniversitySelectModal';

const NavbarContainer = styled.nav`
  background-color: ${props => props.$darkMode ? '#1c1e21' : '#1877f2'};
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 50px;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s ease-out;
  
  /* Ajustar padding en dispositivos móviles */
  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
  
  .logo-icon {
    font-size: 20px;
    margin-top: 2px;
    
    @media (max-width: 768px) {
      font-size: 18px;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  a, button {
    color: white;
    text-decoration: none;
    font-weight: 500;
    white-space: nowrap;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  /* Ajustar para dispositivos móviles: mantener visible pero con menos espacio */
  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const ThemeToggle = styled.div`
  display: flex;
  align-items: center;
  margin: 0 10px;
  
  /* Mantener visible en móvil pero más compacto */
  @media (max-width: 768px) {
    margin: 0 5px;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 52px;
  height: 26px;
  cursor: pointer;
  
  @media (max-width: 768px) {
    width: 40px;
    height: 22px;
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: #2d88ff;
  }
  
  &:checked + span:before {
    transform: translateX(26px);
    
    @media (max-width: 768px) {
      transform: translateX(18px); /* Ajustado para pantallas pequeñas */
    }
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 34px;
  
  &:before {
    content: "";
    position: absolute;
    height: 22px;
    width: 22px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  
  @media (max-width: 768px) {
    &:before {
      height: 18px;
      width: 18px;
    }
  }
`;

const ThemeIcon = styled.div`
  margin: 0 8px;
  color: ${props => props.$icon === 'sun' 
    ? (props.$darkMode ? '#ffffff80' : '#ffffff') 
    : (props.$darkMode ? '#ffffff' : '#ffffff80')};
  font-size: 16px;

  /* Si el icono es de sol y estamos en modo claro, o si es de luna y estamos en modo oscuro, resaltarlo */
  color: ${props => 
    (props.$icon === 'sun' && !props.$darkMode) || 
    (props.$icon === 'moon' && props.$darkMode) 
      ? props.$darkMode ? '#f1c40f' : '#f39c12' 
      : props.theme.textSecondary};
      
  /* Hacer iconos más pequeños en móvil */
  @media (max-width: 768px) {
    margin: 0 4px;
    font-size: 14px;
  }
`;

const UniversityIcon = styled.div`
  width: ${props => props.$size === 'small' ? '20px' : '28px'};
  height: ${props => props.$size === 'small' ? '20px' : '28px'};
  background-color: ${props => props.$color || '#2E7D32'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: ${props => props.$size === 'small' ? '10px' : '14px'};
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    background-color: white;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
  
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
`;

const UniversityInitials = styled.div`
  position: absolute;
  ${props => props.$position === 'top' ? `
    top: -15px;
  ` : `
    bottom: -15px;
  `}
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: bold;
  color: ${props => props.$darkMode ? '#e4e6eb' : '#1c1e21'};
  white-space: nowrap;
  background-color: ${props => props.$darkMode ? 'rgba(36, 37, 38, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  padding: 1px 4px;
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    font-size: 10px;
    font-weight: 600;
    top: -4px;
    bottom: auto;
    background-color: ${props => props.$darkMode ? 'rgba(36, 37, 38, 1)' : 'rgba(255, 255, 255, 1)'};
    padding: 2px 6px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    z-index: 1;
  }
`;

const IconButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 20px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    font-size: 18px;
  }
`;

const AccountButton = styled.div`
  position: relative;
  cursor: pointer;
  z-index: 10;
  margin-left: 10px;
  
  @media (max-width: 768px) {
    margin-left: 5px;
  }
`;

const AccountAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(45deg, #4CAF50, #45a049);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const AccountDropdown = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 240px;
  background-color: ${props => props.$darkMode ? '#242526' : 'white'};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  z-index: 100;
  border: 1px solid ${props => props.$darkMode ? '#3a3b3c' : 'rgba(0, 0, 0, 0.1)'};

  &:before {
    content: '';
    position: absolute;
    top: -6px;
    right: 12px;
    width: 12px;
    height: 12px;
    background: ${props => props.$darkMode ? '#242526' : 'white'};
    transform: rotate(45deg);
    border-left: 1px solid ${props => props.$darkMode ? '#3a3b3c' : 'rgba(0, 0, 0, 0.1)'};
    border-top: 1px solid ${props => props.$darkMode ? '#3a3b3c' : 'rgba(0, 0, 0, 0.1)'};
  }
`;

const AccountOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  color: ${props => props.$darkMode ? '#e4e6eb' : '#1c1e21'};
  background-color: ${props => props.$darkMode ? '#242526' : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.$darkMode ? '#e4e6eb' : '#65676b'};
    transition: all 0.2s ease;
  }
  
  &:hover {
    background-color: ${props => props.$darkMode ? '#3a3b3c' : '#f2f2f2'};
    
    svg {
      color: ${props => props.theme.primary};
      transform: scale(1.1);
    }
  }
`;

const AccountDivider = styled.div`
  height: 1px;
  background-color: ${props => props.$darkMode ? '#3a3b3c' : '#f0f2f5'};
  margin: 8px 0;
`;

const NavigationMenu = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${props => props.$darkMode ? '#242526' : 'white'};
    height: 56px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    z-index: 999;
    justify-content: space-around;
    padding: 0 10px;
  }
`;

const MobileNav = styled(NavigationMenu)`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    padding: 5px 10px;
  }
`;

const NavItem = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 20px;
  color: ${props => props.$active ? (props.$darkMode ? '#2d88ff' : '#1877f2') : (props.$darkMode ? '#b0b3b8' : 'rgba(255, 255, 255, 0.85)')};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)'};
    text-decoration: none;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  @media (max-width: 768px) {
    padding: 8px 0;
    width: 33%;
    border-bottom: none;
    color: ${props => props.$active 
      ? (props.$darkMode ? '#2d88ff' : '#1877f2') 
      : (props.$darkMode ? '#b0b3b8' : '#65676b')};
    
    /* Agregar iluminación de fondo como en el menú lateral */
    background-color: ${props => props.$active 
      ? (props.$darkMode ? 'rgba(45, 136, 255, 0.1)' : 'rgba(24, 119, 242, 0.1)') 
      : 'transparent'};
    
    /* Agregar un borde redondeado para que se vea como los elementos del menú lateral */
    border-radius: 8px;
    margin: 5px;
    
    &:hover {
      background-color: ${props => props.$darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
    }
  }
`;

// Crear un nuevo componente para la navegación lateral en desktop
const SideNavigation = styled.div`
  position: fixed;
  left: 0;
  top: 50px; /* Debajo del navbar */
  width: 70px;
  height: calc(100vh - 50px);
  background-color: ${props => props.$darkMode ? '#242526' : '#ffffff'};
  box-shadow: ${props => props.$darkMode ? 'none' : '1px 0 3px rgba(0, 0, 0, 0.1)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 0;
  z-index: 999;
  transition: background-color 0.2s ease-out;
  
  @media (max-width: 768px) {
    display: none; /* Ocultar en móvil */
  }
`;

// Estilo para los elementos de navegación lateral
const SideNavItem = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 8px;
  margin-bottom: 10px;
  color: ${props => props.$active 
    ? (props.$darkMode ? '#2d88ff' : '#1877f2') 
    : (props.$darkMode ? '#b0b3b8' : '#65676b')};
  background-color: ${props => props.$active 
    ? (props.$darkMode ? 'rgba(45, 136, 255, 0.1)' : 'rgba(24, 119, 242, 0.1)') 
    : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
    text-decoration: none;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

// Nuevo componente para botones que no son enlaces
const SideNavButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 8px;
  margin-bottom: 10px;
  color: ${props => props.$darkMode ? '#b0b3b8' : '#65676b'};
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const AuthButtonContainer = styled.div`
  opacity: ${props => props.$isLoading ? '0' : '1'};
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
`;

function Navbar() {
  const { isAuthenticated, loading, logout } = useAuth();
  const { darkMode, setDarkMode } = useContext(ThemeContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [initialAuthTab, setInitialAuthTab] = useState('login');
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [showUniversityModal, setShowUniversityModal] = useState(false);
  const { currentUniversity, universities, changeUniversity } = useUniversity();
  const [showSubscriptionMessage, setShowSubscriptionMessage] = useState(false);
  const [navbarReady, setNavbarReady] = useState(false);
  
  // Añadir ref para el click outside
  const dropdownRef = useRef(null);
  
  useLayoutEffect(() => {
    const prepareNavbar = async () => {
      await new Promise(resolve => setTimeout(resolve, 50));
      setNavbarReady(true);
    };
    
    prepareNavbar();
    
    return () => {
      setNavbarReady(false);
    };
  }, []);
  
  useEffect(() => {
    if (loading) return;
    
    const timer = setTimeout(() => {
      setIsAuthLoading(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [loading]);
  
  useEffect(() => {
    setAccountMenuOpen(false);
  }, [isAuthenticated]);
  
  // Manejar click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };

    if (accountMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [accountMenuOpen]);
  
  const toggleUniversityModal = () => {
    setShowUniversityModal(!showUniversityModal);
  };
  
  const handleUniversitySelection = (universityId) => {
    changeUniversity(universityId);
    setShowUniversityModal(false);
  };
  
  const handleOpenModal = (tab) => {
    setInitialAuthTab(tab);
    setShowAuthModal(true);
  };

  const handleCloseModal = () => {
    setShowAuthModal(false);
  };
  
  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };
  
  const toggleAccountMenu = () => {
    setAccountMenuOpen(!accountMenuOpen);
  };
  
  const handleSubscriptionClick = () => {
    setAccountMenuOpen(false);
    setShowSubscriptionMessage(true);
    setTimeout(() => setShowSubscriptionMessage(false), 3000);
  };
  
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path) => location.pathname === path;
  
  const handleProfileClick = (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      handleOpenModal('login');
    } else {
      navigate('/profile');
      setAccountMenuOpen(false);
    }
  };
  
  const handleHomeClick = (e) => {
    e.preventDefault();
    navigate('/');
    setAccountMenuOpen(false);
  };
  
  if (!navbarReady) {
    return (
      <NavbarContainer $darkMode={darkMode} style={{ visibility: 'hidden' }}>
        <div></div>
      </NavbarContainer>
    );
  }

  return (
    <>
      <NavbarContainer $darkMode={darkMode}>
        <Logo>
          <Link to="/" style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center' }} onClick={handleHomeClick}>
            <span className="logo-icon">🐸</span>
            Sapotix
          </Link>
        </Logo>
        
        <NavLinks>
          <ThemeToggle>
            <ThemeIcon $icon="sun" $darkMode={darkMode}>
              <FaSun />
            </ThemeIcon>
            <ToggleSwitch>
              <ToggleInput 
                type="checkbox" 
                checked={darkMode}
                onChange={toggleTheme}
                id="theme-toggle"
                name="theme-toggle"
              />
              <ToggleSlider />
            </ToggleSwitch>
            <ThemeIcon $icon="moon" $darkMode={darkMode}>
              <FaMoon />
            </ThemeIcon>
          </ThemeToggle>
          
          <AuthButtonContainer $isLoading={isAuthLoading}>
            {isAuthenticated ? (
              <AccountButton ref={dropdownRef}>
                <AccountAvatar onClick={toggleAccountMenu}>
                  🐸
                </AccountAvatar>
                
                <AccountDropdown 
                  $isOpen={accountMenuOpen} 
                  $darkMode={darkMode}
                >
                  <AccountOption 
                    $darkMode={darkMode} 
                    onClick={handleSubscriptionClick}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <span>Suscripción</span>
                  </AccountOption>
                  
                  <AccountOption 
                    $darkMode={darkMode} 
                    onClick={() => {
                      setShowUniversityModal(true);
                      setAccountMenuOpen(false);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                    <span>Seleccionar Universidad</span>
                  </AccountOption>
                  
                  <AccountDivider $darkMode={darkMode} />
                  
                  <AccountOption 
                    $darkMode={darkMode} 
                    onClick={() => logout()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    <span>Cerrar sesión</span>
                  </AccountOption>
                </AccountDropdown>
              </AccountButton>
            ) : (
              <IconButton onClick={() => handleOpenModal('login')} title="Iniciar sesión / Registrarse">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                  <polyline points="10 17 15 12 10 7"></polyline>
                  <line x1="15" y1="12" x2="3" y2="12"></line>
                </svg>
              </IconButton>
            )}
          </AuthButtonContainer>
        </NavLinks>
      </NavbarContainer>
      
      <SideNavigation $darkMode={darkMode}>
        <SideNavItem 
          to="/" 
          $active={isActive('/')} 
          $darkMode={darkMode}
          onClick={handleHomeClick}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </SideNavItem>
        
        <SideNavItem 
          to="/profile" 
          $active={isActive('/profile')} 
          $darkMode={darkMode}
          onClick={handleProfileClick}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </SideNavItem>
        
        <SideNavButton 
          $darkMode={darkMode}
          onClick={toggleUniversityModal}
        >
          {currentUniversity ? (
            <>
              <UniversityInitials $darkMode={darkMode} $position="bottom">
                {currentUniversity.initials}
              </UniversityInitials>
              <UniversityIcon $color={currentUniversity.color} $size="small">
                {currentUniversity.logo ? (
                  <img 
                    src={currentUniversity.logo}
                    alt={currentUniversity.name}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      borderRadius: '50%',
                      backgroundColor: 'white'
                    }}
                    crossOrigin="anonymous"
                  />
                ) : (
                  <FaUniversity />
                )}
              </UniversityIcon>
            </>
          ) : (
            <FaUniversity />
          )}
        </SideNavButton>
      </SideNavigation>
      
      <MobileNav $darkMode={darkMode}>
        <NavItem 
          to="/" 
          $active={isActive('/')} 
          $darkMode={darkMode}
          onClick={handleHomeClick}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          </svg>
        </NavItem>
        
        <NavItem 
          as="button" 
          $darkMode={darkMode}
          onClick={toggleUniversityModal}
          style={{ 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer',
            width: '33%',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {currentUniversity ? (
            <>
              <UniversityInitials $darkMode={darkMode}>
                {currentUniversity.initials}
              </UniversityInitials>
              <UniversityIcon $color={currentUniversity.color} $size="small">
                {currentUniversity.logo ? (
                  <img 
                    src={currentUniversity.logo}
                    alt={currentUniversity.name}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      borderRadius: '50%',
                      backgroundColor: 'white'
                    }}
                    crossOrigin="anonymous"
                  />
                ) : (
                  <FaUniversity />
                )}
              </UniversityIcon>
            </>
          ) : (
            <FaUniversity />
          )}
        </NavItem>
        
        <NavItem 
          to="/profile" 
          $active={isActive('/profile')} 
          $darkMode={darkMode}
          onClick={handleProfileClick}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </NavItem>
      </MobileNav>
      
      <AuthModal 
        show={showAuthModal} 
        onClose={handleCloseModal} 
        initialTab={initialAuthTab}
      />
      
      <UniversitySelectModal
        show={showUniversityModal}
        onClose={() => setShowUniversityModal(false)}
        onSelect={handleUniversitySelection}
        universities={universities}
        currentUniversityId={currentUniversity?.id || ''}
        onOpenAuthModal={() => handleOpenModal('login')}
      />
      
      {showSubscriptionMessage && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: darkMode ? '#3a3b3c' : '#f0f2f5',
          color: darkMode ? '#e4e6eb' : '#1c1e21',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease',
          border: `1px solid ${darkMode ? '#4a4b4c' : '#dddfe2'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#42b72a' }}>
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span>Las suscripciones estarán disponibles próximamente</span>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;

